(function () {
  'use strict';

  const TOPICS = Object.freeze({
    'topic-safety-recalls': Object.freeze({ label: 'Safety & Recalls' }),
    'topic-veterinary-science': Object.freeze({ label: 'Veterinary & Science' }),
    'topic-pet-tech': Object.freeze({ label: 'Pet Tech' }),
    'topic-market-retail': Object.freeze({ label: 'Market & Retail' }),
    'topic-cross-border-regulation': Object.freeze({ label: 'E-commerce & Regulation' })
  });

  const TOPIC_ALIASES = Object.freeze({
    'topic-pet-safety': 'topic-safety-recalls'
  });
  const DATE_FILTERS = Object.freeze({ today: 'Today', yesterday: 'Yesterday' });
  const DATE_KEY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
  const MAX_ITEMS_PER_COHORT = 24;

  function canonicalTopic(value) {
    if (typeof value !== 'string') return null;
    const candidate = value.trim().toLowerCase();
    const canonical = TOPIC_ALIASES[candidate] || candidate;
    return Object.prototype.hasOwnProperty.call(TOPICS, canonical) ? canonical : null;
  }

  function canonicalDate(value) {
    if (typeof value !== 'string') return null;
    const candidate = value.trim().toLowerCase();
    if (Object.prototype.hasOwnProperty.call(DATE_FILTERS, candidate)) return candidate;
    return isValidDateKey(candidate) ? candidate : null;
  }

  function isValidDateKey(value) {
    if (typeof value !== 'string') return false;
    const match = DATE_KEY_PATTERN.exec(value.trim());
    if (!match) return false;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year
      && date.getUTCMonth() === month - 1
      && date.getUTCDate() === day;
  }

  function parseTimezoneOffset(value) {
    if (typeof value !== 'string') return null;
    const match = /^([+-])(\d{2})(\d{2})$/.exec(value.trim());
    if (!match) return null;
    const hours = Number(match[2]);
    const minutes = Number(match[3]);
    if (hours > 23 || minutes > 59) return null;
    const totalMinutes = (hours * 60) + minutes;
    return match[1] === '-' ? -totalMinutes : totalMinutes;
  }

  function localDateKey(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function dateKeyFor(value, timezoneOffset) {
    if (typeof value !== 'string' || !value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    const offsetMinutes = parseTimezoneOffset(timezoneOffset);
    if (offsetMinutes === null) return localDateKey(date);
    return new Date(date.getTime() + (offsetMinutes * 60 * 1000)).toISOString().slice(0, 10);
  }

  function addDays(dateKey, days) {
    if (!isValidDateKey(dateKey)) return '';
    const date = new Date(`${dateKey}T12:00:00Z`);
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
  }

  function dateFilterTarget(selectedDate, todayDate, timezoneOffset) {
    const canonical = canonicalDate(selectedDate);
    if (!canonical) return null;
    const today = isValidDateKey(todayDate || '')
      ? todayDate
      : dateKeyFor(new Date().toISOString(), timezoneOffset);
    if (canonical === 'today') return today;
    if (canonical === 'yesterday') return addDays(today, -1);
    return canonical;
  }

  function formatDateKey(value) {
    if (!isValidDateKey(value)) return '';
    const language = typeof document !== 'undefined' && document.documentElement
      ? document.documentElement.lang || 'en'
      : 'en';
    try {
      return new Intl.DateTimeFormat(language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }).format(new Date(`${value}T12:00:00Z`));
    } catch {
      return value;
    }
  }

  function dateFilterLabel(selectedDate, todayDate, timezoneOffset) {
    const canonical = canonicalDate(selectedDate);
    if (!canonical) return '';
    if (DATE_FILTERS[canonical]) return DATE_FILTERS[canonical];
    return formatDateKey(dateFilterTarget(canonical, todayDate, timezoneOffset)) || canonical;
  }

  function safeText(value, maxLength) {
    if (typeof value !== 'string') return '';
    return value.replace(/\s+/g, ' ').trim().slice(0, maxLength);
  }

  function normalizeFeedItem(item, origin) {
    if (!item || typeof item !== 'object' || typeof origin !== 'string') return null;

    let url;
    let expectedOrigin;
    try {
      url = new URL(String(item.url || item.id || ''), origin);
      expectedOrigin = new URL(origin).origin;
    } catch {
      return null;
    }

    const source = url.pathname.startsWith('/apps/media/news/')
      ? 'app'
      : url.pathname.startsWith('/blogs/news/')
        ? 'shopify'
        : null;
    if (url.origin !== expectedOrigin || !source) return null;

    const title = safeText(item.title, 180);
    if (!title) return null;

    const tags = Array.isArray(item.tags) ? item.tags : [];
    const topic = tags.map(canonicalTopic).find(Boolean) || null;
    const summary = safeText(item.summary, 420);
    const publishedAt = typeof item.date_published === 'string' && !Number.isNaN(Date.parse(item.date_published))
      ? item.date_published
      : '';
    const modifiedAt = typeof item.date_modified === 'string' && !Number.isNaN(Date.parse(item.date_modified))
      ? item.date_modified
      : publishedAt;

    return Object.freeze({
      url: url.href,
      title,
      summary,
      publishedAt,
      modifiedAt,
      topic,
      source
    });
  }

  function normalizeFeedItems(payload, origin) {
    const sourceItems = payload && Array.isArray(payload.items) ? payload.items : [];
    const seen = new Set();
    const appItems = [];
    const shopifyItems = [];

    for (const sourceItem of sourceItems) {
      const item = normalizeFeedItem(sourceItem, origin);
      if (!item) continue;
      const cohort = item.source === 'app' ? appItems : shopifyItems;
      if (cohort.length >= MAX_ITEMS_PER_COHORT || seen.has(item.url)) continue;
      seen.add(item.url);
      cohort.push(item);
      if (appItems.length >= MAX_ITEMS_PER_COHORT && shopifyItems.length >= MAX_ITEMS_PER_COHORT) break;
    }

    return [...appItems, ...shopifyItems];
  }

  function selectFeedItems(items, selectedTopic, selectedDate, todayDate, timezoneOffset) {
    if (!Array.isArray(items)) return [];
    const targetDate = dateFilterTarget(selectedDate, todayDate, timezoneOffset);
    return items.filter((item) => {
      if (item.source !== 'app') return false;
      if (selectedTopic && item.topic !== selectedTopic) return false;
      return !targetDate || dateKeyFor(item.publishedAt, timezoneOffset) === targetDate;
    });
  }

  function shouldHideLegacy(feedState, selectedTopic, appItemCount) {
    if (feedState === 'unavailable') return false;
    if (feedState === 'ready') return appItemCount > 0;
    return Boolean(selectedTopic);
  }

  function topicHref(blogUrl, topic, selectedDate) {
    const path = typeof blogUrl === 'string' && blogUrl.startsWith('/') ? blogUrl : '/blogs/news';
    const params = new URLSearchParams();
    if (topic) params.set('topic', topic);
    const date = canonicalDate(selectedDate);
    if (date) params.set('date', date);
    const query = params.toString();
    return `${path}${query ? `?${query}` : ''}#news-app-reporting`;
  }

  function createElement(tagName, className, text) {
    const node = document.createElement(tagName);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  function formatDate(value) {
    if (!value) return '';
    try {
      return new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }).format(new Date(value));
    } catch {
      return '';
    }
  }

  function buildCard(item, blogUrl, selectedDate) {
    const article = createElement('article', 'news-card news-card--standard news-card--text-only');
    article.dataset.newsOrigin = 'app-feed';
    if (item.topic) article.dataset.newsTopic = item.topic;

    const content = createElement('div', 'news-card__content');
    const kickers = createElement('div', 'news-card__kickers');

    if (item.topic) {
      const topicLink = createElement('a', 'news-card__topic', TOPICS[item.topic].label);
      topicLink.href = topicHref(blogUrl, item.topic, selectedDate);
      kickers.append(topicLink);
    }

    kickers.append(createElement('span', 'news-card__status', 'News Desk'));
    content.append(kickers);

    const heading = createElement('h3', 'news-card__title');
    const storyLink = createElement('a', '', item.title);
    storyLink.href = item.url;
    heading.append(storyLink);
    content.append(heading);

    if (item.summary) content.append(createElement('p', 'news-card__dek', item.summary));

    const formattedDate = formatDate(item.publishedAt);
    if (formattedDate) {
      const meta = createElement('div', 'news-card__meta');
      const time = createElement('time', '', formattedDate);
      time.dateTime = item.publishedAt;
      meta.append(time);
      content.append(meta);
    }

    article.append(content);
    return article;
  }

  function updateTopicNavigation(selectedTopic) {
    document.querySelectorAll('[data-news-topic-link]').forEach((link) => {
      const linkTopic = canonicalTopic(link.dataset.newsTopicLink || '');
      const isCurrent = selectedTopic ? linkTopic === selectedTopic : !linkTopic;
      if (isCurrent) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });
  }

  function updateDateNavigation(selectedDate, todayDate, timezoneOffset) {
    const canonical = canonicalDate(selectedDate);
    const targetDate = dateFilterTarget(canonical, todayDate, timezoneOffset);
    document.querySelectorAll('[data-news-date-link]').forEach((link) => {
      const linkDate = canonicalDate(link.dataset.newsDateValue || '');
      const isCurrent = canonical ? linkDate === canonical : !linkDate;
      link.setAttribute('aria-pressed', String(isCurrent));
    });

    document.querySelectorAll('[data-news-date-input]').forEach((input) => {
      input.value = canonical && !DATE_FILTERS[canonical] ? canonical : '';
    });

    document.querySelectorAll('[data-news-date-status]').forEach((status) => {
      if (!canonical) {
        status.hidden = true;
        status.textContent = '';
        return;
      }
      const label = DATE_FILTERS[canonical]
        ? DATE_FILTERS[canonical].toLowerCase()
        : formatDateKey(targetDate) || targetDate;
      status.hidden = false;
      status.textContent = targetDate
        ? `Showing reporting from ${label} (${targetDate}).`
        : `Showing reporting from ${label}.`;
    });
  }

  function bindTopicNavigation(root) {
    const landing = root.closest('.news-index');
    if (!landing) return;
    const selectedDate = canonicalDate(new URL(window.location.href).searchParams.get('date'));
    if (!selectedDate) return;

    landing.querySelectorAll('[data-news-topic-link]').forEach((link) => {
      if (link.dataset.newsDatePreserved === 'true') return;
      const href = new URL(link.href, window.location.origin);
      href.searchParams.set('date', selectedDate);
      link.href = `${href.pathname}${href.search}${href.hash}`;
      link.dataset.newsDatePreserved = 'true';
    });
  }

  function updateDefaultView(root, hideLegacy) {
    const landing = root.closest('.news-index');
    if (!landing) return;
    landing.dataset.newsLegacyHidden = hideLegacy ? 'true' : 'false';
    landing.querySelectorAll('[data-news-default-view]').forEach((element) => {
      element.hidden = hideLegacy;
    });
  }

  function renderFeedStructuredData(root, items) {
    const landing = root.closest('.news-index');
    if (!landing) return;
    const appItems = items.filter((item) => item.source === 'app');
    let script = landing.querySelector('[data-news-app-schema]');

    if (appItems.length === 0) {
      if (script) script.remove();
      return;
    }

    if (!script) {
      script = createElement('script');
      script.type = 'application/ld+json';
      script.dataset.newsAppSchema = 'true';
      landing.append(script);
    }

    const pageUrl = root.dataset.newsSchemaUrl || `${window.location.origin}${window.location.pathname}`;
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      '@id': `${pageUrl}#news-app-item-list`,
      name: root.dataset.newsSchemaName || 'Latest reporting',
      numberOfItems: appItems.length,
      itemListElement: appItems.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'NewsArticle',
          '@id': `${item.url}#news-article`,
          url: item.url,
          headline: item.title,
          description: item.summary || undefined,
          datePublished: item.publishedAt || undefined,
          dateModified: item.modifiedAt || undefined,
          articleSection: item.topic && TOPICS[item.topic] ? TOPICS[item.topic].label : undefined,
          author: { '@type': 'Organization', name: root.dataset.newsSchemaName || 'News Desk' },
          publisher: { '@type': 'Organization', name: root.dataset.newsSchemaPublisher || 'News Desk', url: window.location.origin }
        }
      }))
    });
  }

  function renderFeed(root, payload, selectedTopic, selectedDate, todayDate, timezoneOffset) {
    const list = root.querySelector('[data-news-app-list]');
    const status = root.querySelector('[data-news-app-status]');
    const heading = root.querySelector('[data-news-app-heading]');
    if (!list || !status || !heading) return;

    const items = normalizeFeedItems(payload, window.location.origin);
    const visibleItems = selectFeedItems(items, selectedTopic, selectedDate, todayDate, timezoneOffset);
    const blogUrl = root.dataset.blogUrl || '/blogs/news';
    const canonicalSelectedDate = canonicalDate(selectedDate);
    const dateLabel = dateFilterLabel(canonicalSelectedDate, todayDate, timezoneOffset);
    const dateHeading = canonicalSelectedDate
      ? DATE_FILTERS[canonicalSelectedDate] || `Reporting on ${dateLabel}`
      : '';

    list.replaceChildren();
    list.dataset.newsItemCount = String(visibleItems.length);
    root.dataset.newsDateFilter = canonicalDate(selectedDate) || '';
    renderFeedStructuredData(root, items);
    heading.textContent = selectedTopic
      ? `Latest in ${TOPICS[selectedTopic].label}${dateHeading ? ` · ${dateHeading}` : ''}`
      : dateHeading
        ? DATE_FILTERS[canonicalSelectedDate] ? `${dateHeading} reporting` : dateHeading
        : 'Latest reporting';

    if (visibleItems.length === 0) {
      updateDefaultView(root, shouldHideLegacy('empty', selectedTopic, 0));
      root.dataset.feedState = 'empty';
      const dateStatusLabel = DATE_FILTERS[canonicalSelectedDate]
        ? dateLabel.toLowerCase()
        : dateLabel;
      status.textContent = selectedTopic
        ? `No current ${TOPICS[selectedTopic].label} reports${dateStatusLabel ? ` for ${dateStatusLabel}` : ''} are available yet.`
        : `No current media archive reports${dateStatusLabel ? ` for ${dateStatusLabel}` : ''} are available. More News Desk stories remain available below.`;
      status.hidden = false;
      list.hidden = true;
      return;
    }

    const fragment = document.createDocumentFragment();
    visibleItems.forEach((item) => fragment.append(buildCard(item, blogUrl, selectedDate)));
    updateDefaultView(root, shouldHideLegacy('ready', selectedTopic, visibleItems.length));
    list.append(fragment);
    list.hidden = false;
    status.hidden = true;
    root.dataset.feedState = 'ready';
  }

  function applyDateSelection(root, selectedDate, todayDate, timezoneOffset) {
    const url = new URL(window.location.href);
    url.searchParams.delete('date');
    if (selectedDate) url.searchParams.set('date', selectedDate);
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    window.dispatchEvent(new CustomEvent('news:date-filter-change', {
      detail: { date: selectedDate || 'all' }
    }));
    updateDateNavigation(selectedDate, todayDate, timezoneOffset);
    const selectedTopic = canonicalTopic(url.searchParams.get('topic'));
    if (root.__newsFeedPayload) {
      renderFeed(root, root.__newsFeedPayload, selectedTopic, selectedDate, todayDate, timezoneOffset);
    }
  }

  function bindDateNavigation(root, todayDate, timezoneOffset) {
    const landing = root.closest('.news-index');
    if (!landing) return;
    landing.querySelectorAll('[data-news-date-link]').forEach((link) => {
      if (link.dataset.newsDateBound === 'true') return;
      link.dataset.newsDateBound = 'true';
      link.addEventListener('click', () => {
        const selectedDate = canonicalDate(link.dataset.newsDateValue || '');
        applyDateSelection(root, selectedDate, todayDate, timezoneOffset);
      });
    });

    const form = landing.querySelector('[data-news-date-form]');
    const input = landing.querySelector('[data-news-date-input]');
    if (form && input && form.dataset.newsDateBound !== 'true') {
      form.dataset.newsDateBound = 'true';
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const selectedDate = canonicalDate(input.value);
        if (!selectedDate || DATE_FILTERS[selectedDate]) {
          input.setCustomValidity('Choose a valid reporting date.');
          input.reportValidity();
          return;
        }
        input.setCustomValidity('');
        applyDateSelection(root, selectedDate, todayDate, timezoneOffset);
      });
      input.addEventListener('input', () => input.setCustomValidity(''));
    }
  }

  async function initializeFeed(root) {
    const feedUrl = root.dataset.feedUrl;
    const url = new URL(window.location.href);
    const selectedTopic = canonicalTopic(url.searchParams.get('topic'));
    const selectedDate = canonicalDate(url.searchParams.get('date'));
    const landing = root.closest('.news-index');
    const todayDate = root.dataset.newsToday || (landing ? landing.dataset.newsToday : '') || '';
    const timezoneOffset = root.dataset.newsTimezoneOffset || (landing ? landing.dataset.newsTimezoneOffset : '') || '';
    updateTopicNavigation(selectedTopic);
    bindTopicNavigation(root);
    updateDateNavigation(selectedDate, todayDate, timezoneOffset);
    bindDateNavigation(root, todayDate, timezoneOffset);
    updateDefaultView(root, shouldHideLegacy('loading', selectedTopic, 0));

    try {
      const response = await fetch(feedUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/feed+json, application/json' }
      });
      if (!response.ok) throw new Error(`feed returned ${response.status}`);
      root.__newsFeedPayload = await response.json();
      renderFeed(root, root.__newsFeedPayload, selectedTopic, selectedDate, todayDate, timezoneOffset);
    } catch {
      const status = root.querySelector('[data-news-app-status]');
      updateDefaultView(root, shouldHideLegacy('unavailable', selectedTopic, 0));
      root.dataset.feedState = 'unavailable';
      if (status) status.textContent = 'Latest reporting is temporarily unavailable. More News Desk stories remain available below.';
    }
  }

  const api = Object.freeze({ addDays, canonicalDate, canonicalTopic, dateFilterLabel, dateFilterTarget, dateKeyFor, formatDateKey, isValidDateKey, normalizeFeedItem, normalizeFeedItems, renderFeedStructuredData, selectFeedItems, shouldHideLegacy, topicHref });
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') window.VepNewsAppFeed = api;

  if (typeof document === 'undefined') return;
  const start = () => document.querySelectorAll('[data-news-app-feed]').forEach(initializeFeed);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
